import { useContext } from 'react';
import { FlowForm, useFlowFormGlobal, FlowFormLeaveContext, getKeyHandler } from 't-lib-ui/structure';
import { A } from 't-lib-ui/widgets';
import { RouterContext, goTo, useParams } from 't-lib-ui/router';
import { sidebarLeaveHandler } from '../components/LeftSidebarFocus';
import MainLayout from '../components/MainLayout';
import PageHeader from '../components/PageHeader';
import PageIntro from '../components/PageIntro';
import OptionCard from '../components/OptionCard';
import QuestionMarkIcon from '../components/QuestionMarkIcon';
import ChevronLeftIcon from '../components/ChevronLeftIcon';
import PrivateFeedIcon from '../components/PrivateFeedIcon';
import MailIcon from '../components/MailIcon';
import './HelpPage.css';

// Consistent stroke-based icon set for the topic cards (matching the app's SVG-only icon style).
const svg = (children: React.ReactNode, size = 26) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    {children}
  </svg>
);

const GlobeLockIcon = () => svg(<>
  <circle cx="12" cy="12" r="9" />
  <path d="M3 12h18" />
  <ellipse cx="12" cy="12" rx="4" ry="9" />
</>);

const UnreadCountIcon = () => svg(<>
  <rect x="3" y="5" width="13" height="15" rx="2" />
  <path d="M6 10h7M6 13h7M6 16h4" />
  <circle cx="18" cy="6.5" r="3.5" fill="currentColor" stroke="none" />
</>);

// The three post actions, in the order they appear on a post: lock (mark read / keep unread),
// the read-later clock, and the star.
const ThreeSymbolsIcon = () => (
  <svg width={26} height={26} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2.4" y="11" width="5" height="4.6" rx="0.9" />
    <path d="M3.5 11V9.8a1.4 1.4 0 0 1 2.8 0V11" />
    <circle cx="12" cy="12.7" r="2.9" />
    <path d="M12 11.3v1.5l1 .7" />
    <path d="M19 9.6l.84 1.7 1.86.27-1.35 1.31.32 1.86L19 13.93l-1.67.88.32-1.86-1.35-1.31 1.86-.27z" fill="currentColor" stroke="none" />
  </svg>
);

const KeyboardIcon = () => svg(<>
  <rect x="2.5" y="6" width="19" height="12" rx="2" />
  <path d="M6 10h.01M9.5 10h.01M13 10h.01M16.5 10h.01M6 13.5h.01M16.5 13.5h.01" />
  <path d="M9.2 13.5h5.6" />
</>);

const EnvelopeIcon = () => svg(<>
  <rect x="3" y="5" width="18" height="14" rx="2" />
  <path d="M3 7l9 6 9-6" />
</>);

const OrganizeIcon = () => svg(<>
  <path d="M10 6h10M10 12h10M10 18h7" />
  <path d="M4 5.4l1.3 1.4L7.6 4.3M4 11.4l1.3 1.4L7.6 10.3M4 17.4l1.3 1.4L7.6 16.3" />
</>);

const MobileIcon = () => svg(<>
  <rect x="7" y="3" width="10" height="18" rx="2.2" />
  <path d="M11 17.6h2" />
</>);

const LifeRingIcon = () => svg(<>
  <circle cx="12" cy="12" r="9" />
  <circle cx="12" cy="12" r="3.6" />
  <path d="M12 3v5M12 16v5M3 12h5M16 12h5" />
</>);

const HomeIcon = () => svg(<>
  <path d="M4 11.5L12 4l8 7.5" />
  <path d="M6 10.5V19h12v-8.5" />
</>);

// Classic RSS mark (broadcast dot + two arcs) in the same stroke style as the
// other topic icons, since "How does it work?" introduces the feed reader.
const RssIcon = () => svg(<>
  <path d="M5 11a8 8 0 0 1 8 8" />
  <path d="M5 5a14 14 0 0 1 14 14" />
  <circle cx="6" cy="18" r="1.3" fill="currentColor" stroke="none" />
</>);

// The exact symbols shown on a post's action bar (see svg-helpers' createLockSvg/createClockSvg),
// reused here so the help matches what the user actually sees on a post.
const LockSymbol: React.FC<{ size?: number }> = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
    <rect x="3" y="5.5" width="10" height="8" rx="1.5" />
    <path d="M5.5 5.5V3.5a2.5 2.5 0 0 1 5 0v2" strokeLinecap="round" />
  </svg>
);

const ClockSymbol: React.FC<{ size?: number }> = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="8" cy="8" r="7" />
    <path d="M8 4 L8 8 L11 10" />
  </svg>
);

const StarSymbol: React.FC = () => <span className="helpStarGlyph">★</span>;

// A non-interactive replica of the three action buttons at the top of a post.
const PostSymbolsDemo: React.FC = () => (
  <div className="helpSymbolDemo" aria-hidden="true">
    <span className="helpSymbolBtn"><LockSymbol /></span>
    <span className="helpSymbolBtn"><ClockSymbol /></span>
    <span className="helpSymbolBtn"><StarSymbol /></span>
  </div>
);

// Framed screenshot snippet (captured from the live app, stored in /public/help).
// The portrait feed list gets a narrower cap so it doesn't tower over the text.
const HelpShot: React.FC<{ src: string; alt: string; maxWidth?: number }> = ({ src, alt, maxWidth = 460 }) => (
  <span className="helpShot" style={{ maxWidth: `${maxWidth}px` }}>
    <img src={src} alt={alt} loading="lazy" />
  </span>
);

interface HelpTopic {
  id: string;
  title: string;
  subtitle: string;
  icon: React.ReactNode;
  body: React.ReactNode;
}

const TOPICS: HelpTopic[] = [
  {
    id: 'intro',
    title: 'How does it work?',
    subtitle: 'A quick introduction to Downread',
    icon: <RssIcon />,
    body: (
      <>
        <p>
          Downread is a <strong>feed reader</strong>. Instead of visiting a dozen websites to check
          what's new, you <strong>follow</strong> them here and read their latest posts together in
          one place - blogs, news sites, magazines, even YouTube channels. You'll spend less time clicking around,
          and more time reading the things you care about.
        </p>
        <h4 style={{ marginBottom: '4px' }}>1. Add the sites you like</h4>
        <HelpShot src="/help/discover.webp" alt="The Discover page showing hand-picked feed suggestions" />
        <p>
          Browse hand-picked suggestions in <A className="helpLink" href="/a/discover" internal>Discover</A>, or
          paste any site or feed URL into <A className="helpLink" href="/a/add" internal>Add Feed</A>. New to all
          this? On the home page just tap <em>“Just try it”</em> or pick a few topics, and we'll set
          you up in a moment.
        </p>
        <h4 style={{ marginBottom: '4px' }}>2. Read everything in one place</h4>
        <HelpShot src="/help/feed.webp" alt="A feed listing its newest posts, with unread markers" maxWidth={340} />
        <p>
          Each feed gathers the newest posts from its site. Or open <A className="helpLink" href="/a/new" internal>Unread posts</A> to
          read what's new across all your feeds, newest first. Posts are marked read as you scroll
          past them, so you only ever see what you haven't read yet.
        </p>
        <h4 style={{ marginBottom: '4px' }}>3. Keep what matters</h4>
        <HelpShot src="/help/post-actions.webp" alt="A post header with the lock, read-later and star buttons" />
        <p>
          Star a post to keep it forever, save it to Read Later to finish when you have time, or lock
          it to keep it unread for your next visit. The <A className="helpLink" href="/a/help/three-symbols" internal>three symbols</A> topic
          explains how to use them in detail.
        </p>
        <p>
          Everything works from the <A className="helpLink" href="/a/help/keyboard" internal>keyboard</A> and on your 
          <A className="helpLink" href="/a/help/mobile" internal>phone</A>. And once you create a free account,
          your feeds and reading sync across all your devices. Smartphones, tablets, laptops or widescreen desktop - 
          Downread optimizes for every screen.
        </p>
      </>
    ),
  },
  {
    id: 'public-private',
    title: 'Public and Private feeds',
    subtitle: "What's the difference?",
    icon: <GlobeLockIcon />,
    body: (
      <>
        <h4 style={{ marginBottom: '4px'}}>Public feeds</h4>
        <p>
          Most feeds in Downread are <strong>public</strong>: ordinary feeds published
          by websites and blogs. Because they're the same for everyone, Downread fetches each one
          just once and shares it across all readers - that's what keeps the service fast and
          inexpensive, and it's how feeds end up in <A className="helpLink" href="/a/discover" internal>Discover</A> and
          <A className="helpLink" href="/a/add" internal>Add Feed</A>'s catalogs.<br/>
          If you add a feed by URL, you can declare your feed as public or private.
          Public feeds will, after review, be added to the public catalog and be visible to other users. 
        </p>
        <h4 style={{ marginBottom: '4px'}}>Private feeds</h4>
        <p>
          A <strong>private feed</strong> is one only you can see. We display them with a <span className="helpBadge"><PrivateFeedIcon size={13} /></span> badge.
          Use this for paid feeds or any feed that you can personalize. 
          These feeds do not show in Downread's public areas. The only way to get them
          is to use the exact URL.
        </p>
        <h4 style={{ marginBottom: '4px'}}>Newsletters</h4>
        <p>
          In paid plans, we also offer <strong>private email inboxes</strong> for newsletters.
          They look like a normal feed in your library, but they have a unique email address you 
          can subscribe to newsletters with. All mails you receive end up as posts in that feed.
          Note that you can subscribe to more than one newsletter with the same inbox address, 
          but all posts from that inbox will be mixed together in the same feed. <br/>
          Newsletter inboxes have a <span className="helpBadge"><MailIcon size={13} /></span> badge.
        </p>
      </>
    ),
  },
  {
    id: 'unread-tracking',
    title: 'Track unread counts',
    subtitle: 'When to say yes',
    icon: <UnreadCountIcon />,
    body: (
      <>
        <p>
          There are two ways to read a feed. A feed that <strong>tracks unread counts</strong> shows
          a number badge and remembers which posts you've read, so you can work through everything
          and reach inbox-zero. A feed <strong>without tracking</strong> behaves like a river of news:
          it shows a simple dot rather than a count, and you just dip in whenever you like.
        </p>
        <p><strong>Turn tracking on</strong> for feeds where you don't want to miss anything: your
          favourite writer, a low-volume blog, a project you follow closely.</p>
        <p><strong>Leave it off</strong> for high-volume or low-priority sources, busy news sites,
          aggregators, anything you only browse occasionally. It keeps your unread totals meaningful.</p>
        <p>
          You can switch this per feed while you're reading it, or on the <A className="helpLink" href="/a/myfeeds" internal>My Feeds</A> page.
          If a backlog ever gets out of hand, the RSS Bankruptcy helper can switch noisy
          feeds to untracked in bulk.
        </p>
      </>
    ),
  },
  {
    id: 'three-symbols',
    title: 'The three symbols',
    subtitle: 'How to quickly organize posts',
    icon: <ThreeSymbolsIcon />,
    body: (
      <>
        <p>
          Every post carries three small symbols, shown at top and bottom:           
        </p>
        <p style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginTop: '1rem' }}>
          <PostSymbolsDemo />
        </p>
        <p>They help you organize the posts you want to come back to later. Their meaning:
        </p>
        <ul className="helpList helpSymbolList">
          <li>
            <span className="helpInlineSymbol"><LockSymbol size={20} /></span>
            <span className="helpSymbolText"><strong>Keep</strong> the post unread by <em>locking</em> it. Posts are marked as read 
            as you scroll past them, so they won't show up next time. Click the 
            'Keep' lock symbol on posts you want to read on your next visit.</span>
          </li>
          <li>
            <span className="helpInlineSymbol"><ClockSymbol size={20} /></span>
            <span className="helpSymbolText"><strong>Read later</strong> with the <em>clock</em>: it sets a post aside to finish when you
            have time. Saved posts collect in your
            {' '}<A className="helpLink" href="/a/read-later" internal>Read later</A> list.</span>
          </li>
          <li>
            <span className="helpInlineSymbol helpInlineStar"><StarSymbol /></span>
            <span className="helpSymbolText"><strong>Star</strong> a post to keep it for good. Starred posts stay on the
            {' '}<A className="helpLink" href="/a/starred" internal>Starred</A> page no matter how old they get.</span>
          </li>
        </ul>
        <p style={{ fontStyle: 'italic', display: 'flex', flexDirection: 'column', alignItems: 'center', marginTop: '1rem' }}>
          <cite>Keep for next visit, Read Later for someday, Star for forever.</cite>
        </p>

      </>
    ),
  },
  {
    id: 'keyboard',
    title: 'Keyboard only',
    subtitle: 'Using Downread without mouse or touchscreen',
    icon: <KeyboardIcon />,
    body: (
      <>
        <p>
          Downread is built to be driven entirely from the keyboard. You can move through every page,
          feed and post without ever touching a mouse or trackpad.
        </p>
        <ul className="helpList">
          <li>Move around with the <kbd className="helpKbd">arrow keys</kbd> and confirm with <kbd className="helpKbd">Return</kbd>.</li>
          <li>Jump between posts with <kbd className="helpKbd">j</kbd> (next) and <kbd className="helpKbd">k</kbd> (previous).</li>
          <li>Press <kbd className="helpKbd">?</kbd> on any page to see every shortcut available there.</li>
          <li>Press <kbd className="helpKbd">Esc</kbd> to close a dialog or step back out.</li>
        </ul>
        <p>
          Focus is always shown clearly when you're using the keyboard, and hidden again the moment
          you reach for the mouse, so the interface stays clean either way.
        </p>
      </>
    ),
  },
  {
    id: 'newsletters',
    title: 'Newsletter inboxes',
    subtitle: 'Reading newsletter emails',
    icon: <EnvelopeIcon />,
    body: (
      <>
        <p>
          Newsletters are some of the best writing on the web, but they clutter your email. Downread
          gives you a <strong>private inbox address</strong> you can use to subscribe to any
          newsletter. The issues then arrive here as a feed, alongside everything else you read.
        </p>
        <p>
          Create one from <A className="helpLink" href="/a/add/newsletter" internal>Add newsletter</A>.
          A newsletter inbox is a private feed: only you can see it, and it never appears in Discover.
        </p>
        <p>
          You read newsletter issues exactly like any other post: <strong>star</strong> them, <strong>save</strong> them for later, or
          <strong>mark</strong> them read and your email stays uncluttered.
        </p>
      </>
    ),
  },
  {
    id: 'organizing',
    title: 'Organizing feeds',
    subtitle: 'Mass operations and more',
    icon: <OrganizeIcon />,
    body: (
      <>
        <p>
          The <A className="helpLink" href="/a/myfeeds" internal>My Feeds</A> page is where you keep your
          library in order. Rename feeds, sort them into folders, and reorder them to taste.
        </p>
        <p>
          It also supports <strong>mass operations</strong>: select several feeds at once to mark them
          all as read, turn unread tracking on or off across the board, move them into a folder, or
          unsubscribe in one go, far quicker than touching each feed individually.
        </p>
        <p>
          Each feed has its own settings too, including its default layout and whether it tracks unread
          counts, so you can tune busy and quiet sources differently.
        </p>
      </>
    ),
  },
  {
    id: 'mobile',
    title: 'Mobile devices',
    subtitle: 'How to use on phones and tablets',
    icon: <MobileIcon />,
    body: (
      <>
        <p>
          Downread works on any device that has a web browser. Just open 
          {' '}<a href="https://downread.com">Downread.com</a> and log in. Your feeds will be available on every device. 
        </p>
        <p>
          For the best experience, <strong>add Downread to your home screen</strong>. In your mobile
          browser's share or menu, choose "Add to Home Screen"; it then launches full-screen like a
          native app.
        </p>
        <p>
          All your feeds, stars and read state sync across every device, so you can start reading on
          your phone and pick up later on your computer.
        </p>
      </>
    ),
  },
  {
    id: 'bankruptcy',
    title: 'RSS bankruptcy',
    subtitle: 'What is happening here?',
    icon: <LifeRingIcon />,
    body: (
      <>
        <p>
          If you've been away for a while, unread posts can pile up into the thousands. Trying to read
          them all is hopeless, so Downread offers to help you <strong>declare RSS bankruptcy</strong> and
          wipe the slate clean.
        </p>
        <p>When a big backlog builds up, a card appears on Home to catch up:</p>
        <ul className="helpList">
          <li>Mark posts older than a few days as read, while keeping the most recent handful per feed.</li>
          <li>Convert your busiest feeds to untracked, so they stop counting unread posts.</li>
          <li>Or go all the way and mark everything as read.</li>
        </ul>
        <p>
          It's entirely optional, of course. You can also turn it off for good in
          {' '}<A className="helpLink" href="/a/preferences" internal>Preferences</A>. And nothing is ever deleted;
          posts are only marked as read.
        </p>
      </>
    ),
  },
];

const HelpPage: React.FC = () => {
  useFlowFormGlobal();
  const routerCtx = useContext(RouterContext);
  // The open topic lives in the URL (/a/help/{topic}) so each topic is directly linkable.
  const { topic: topicId } = useParams<{ topic?: string }>();

  const topic = TOPICS.find(t => t.id == topicId);
  const openTopic = (id: string) => goTo(routerCtx, `/a/help/${id}`);
  const allTopics = () => goTo(routerCtx, '/a/help');
  const goHome = () => goTo(routerCtx, '/a/home');

  return (
    <FlowFormLeaveContext.Provider value={sidebarLeaveHandler}>
      <MainLayout
        selectedFeed="help"
        right={({ onExpand, isMobile }) => (
          <div className="helpPage">
            <PageHeader title={topic ? topic.title : 'Help'} onMenu={onExpand} onBack={topic ? allTopics : goHome} />

            {!topic && (
              <div className="helpIntro">
                <PageIntro
                  icon={<QuestionMarkIcon size={24} />}
                  title="Help"
                  subtitle="Learn how Downread works - pick a topic to read more."
                />
              </div>
            )}
            <FlowForm main className="helpContent">
              <div className="helpInner">
              {!topic ? (
                <>
                  <div className="helpTopicsGrid">
                    {TOPICS.filter(t => !isMobile || t.id != 'keyboard').map(t => (
                      <OptionCard
                        key={t.id}
                        icon={t.icon}
                        title={t.title}
                        description={t.subtitle}
                        onClick={() => openTopic(t.id)}
                      />
                    ))}
                    <OptionCard
                      className="helpHomeCard"
                      icon={<HomeIcon />}
                      title="Back to homepage"
                      description="Return to your reading."
                      onClick={goHome}
                    />
                  </div>
                </>
              ) : (
                <>
                  <div className="helpDetailNav">
                    <button
                      type="button"
                      tabIndex={0}
                      className="helpBackButton flowFocussable flow2d"
                      onClick={allTopics}
                      onKeyDown={getKeyHandler(allTopics)}
                    >
                      <ChevronLeftIcon size={14} />
                      <span>All topics</span>
                    </button>
                    <button
                      type="button"
                      tabIndex={0}
                      className="helpHomeButton flowFocussable flow2d sidewayFocus"
                      onClick={goHome}
                      onKeyDown={getKeyHandler(goHome)}
                    >
                      <HomeIcon />
                      <span>Homepage</span>
                    </button>
                  </div>
                  <div className="helpDetail">
                    <div className="helpDetailHeader">
                      <span className="helpDetailIcon">{topic.icon}</span>
                      <div>
                        <h2 className="helpDetailTitle">{topic.title}</h2>
                        <p className="helpDetailSubtitle">{topic.subtitle}</p>
                      </div>
                    </div>
                    <div className="helpDetailBody">{topic.body}</div>
                    <div className="helpDetailFooter">
                      <OptionCard
                        icon={<QuestionMarkIcon size={24} />}
                        title="All help topics"
                        description="Browse every topic."
                        onClick={allTopics}
                      />
                      <OptionCard
                        icon={<HomeIcon />}
                        title="Back to homepage"
                        description="Return to your reading."
                        onClick={goHome}
                      />
                    </div>
                  </div>
                </>
              )}
              </div>
            </FlowForm>
          </div>
        )}
        startWithLeftCollapsed={false}
      />
    </FlowFormLeaveContext.Provider>
  );
};

export default HelpPage;
